import React from 'react';
import { useTranslation } from 'react-i18next';
import { cefrForPercentage } from 'modules/test/category-scoring';
import { toneForPercent } from './accents';
import { toMinutes, type CategoryResultData } from './categoryResult';
import { AnswerMap, MeterRow, ResultCard, type MapState } from './primitives';
import { RESULT_ACCENTS, type ResultCategoryKey } from './accents';

interface ResultInsightsProps {
  category: ResultCategoryKey;
  data: CategoryResultData;
  /** Taps a cell in the answer map: jump to that question's review. */
  onSelectQuestion?: (n: number) => void;
  className?: string;
}

/** Topics, time and the answer map under a category hero. */
export function ResultInsights({ category, data, onSelectQuestion, className }: ResultInsightsProps) {
  const { t } = useTranslation();
  const accent = RESULT_ACCENTS[category];
  const stateLabels: Record<MapState, string> = {
    ok: t('result.view.correct'),
    no: t('result.view.wrong'),
    skip: t('result.view.skipped'),
  };
  const minutes = toMinutes(data.answeredSeconds);
  const weakest = data.topics.length > 1 ? [...data.topics].sort((a, b) => a.percent - b.percent)[0] : null;

  return (
    <div className={className}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
        <ResultCard accent={accent} title={t('result.view.answer_map')} subtitle={t('result.view.answer_map_hint')}>
          <AnswerMap cells={data.cells} marks={data.cells.length <= 40} onSelect={onSelectQuestion} stateLabels={stateLabels} className="!grid-cols-5 sm:!grid-cols-8 md:!grid-cols-10 lg:!grid-cols-10" />
          <div className="mt-4 flex flex-wrap gap-4 text-[12.5px] font-bold text-[#4B5468]">
            <span className="text-[#166534]">✓ {t('result.view.correct')}</span>
            <span className="text-[#B42318]">✗ {t('result.view.wrong')}</span>
            <span>– {t('result.view.skipped')}</span>
          </div>
        </ResultCard>

        {data.topics.length > 0 && (
          <ResultCard accent={accent} title={t('result.view.by_topic')} subtitle={t('result.view.by_topic_hint')}>
            <div className="mt-2">
              {data.topics.map((topic) => (
                <MeterRow
                  key={topic.id}
                  label={topic.title}
                  value={category === 'tillar' ? `${cefrForPercentage(topic.percent)} · ${topic.percent}%` : `${topic.correct} / ${topic.total}`}
                  percent={topic.percent}
                  color={toneForPercent(topic.percent)}
                  note={weakest && weakest.id === topic.id && topic.percent < 60 ? t('result.view.weakest_topic') : undefined}
                />
              ))}
            </div>
          </ResultCard>
        )}
      </div>
      {minutes > 0 && <p className="mt-3 text-[12.5px] text-[#646D84]">{t('result.view.time_total', { minutes })}</p>}
    </div>
  );
}
